<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { z } from 'zod';
import { 
    UiForm, 
    UiFormField, 
    UiInputField, 
    UiButton, 
    UiText, 
    AmbientFabricBackground,
    UiLogo,
    useToaster 
} from '@shopexperts/shared';
import { useAuthStore } from '@/store/auth';
import { redirectToDashboard } from '@/router/index';
import { useAcceptInvite } from '@/app/composables/useAcceptInvite';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const toaster = useToaster();

// Get token from URL query parameter
const token = computed(() => route.query.token as string | undefined);

// Use the composable
const {
    inviteData,
    isValidating,
    isValidationError,
    validationError,
    accept,
    isAccepting,
} = useAcceptInvite(token);

// Form model
const formModel = ref({
    password: '',
    password_confirmation: '',
});

// Form validation schema
const formSchema = z.object({
    password: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .min(1, 'Password is required')
            .min(8, 'Password must be at least 8 characters')
    ),
    password_confirmation: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string().min(1, 'Please confirm your password')
    ),
}).refine(data => data.password === data.password_confirmation, {
    message: "Passwords don't match",
    path: ['password_confirmation'],
});

const submitRef = useTemplateRef<HTMLButtonElement>('submitRef');

// Computed state for loading
const isLoading = computed(() => isAccepting.value);

// Error message extraction
const errorMessage = computed(() => {
    if (!validationError.value) return null;
    const error = validationError.value as any;
    return error?.response?.data?.message || error?.message || 'Invalid invitation link.';
});

// Check if it's a "user exists" error that should show login link
const isUserExistsError = computed(() => {
    const msg = errorMessage.value?.toLowerCase() || '';
    return msg.includes('already exists') || msg.includes('login instead');
});

async function onSubmit(values: { password: string; password_confirmation: string }) {
    try {
        const result = await accept({
            password: values.password,
            password_confirmation: values.password_confirmation,
        });
        
        // Store credentials and redirect to dashboard
        if (result.token && result.user) {
            // Map the user to include role_id for the auth store
            const userWithRole = {
                ...result.user,
                role_id: 2, // Client role
            };
            authStore.setCurrentUser(userWithRole, result.token);
            
            toaster.create({
                title: 'Welcome!',
                description: 'Your account has been created successfully.',
                type: 'success',
            });
            
            redirectToDashboard(userWithRole, router);
        }
    } catch (error: any) {
        console.error('[AcceptInvite] Accept error:', error);
        // Error handling is done in the composable
    }
}

function handleSubmit() {
    submitRef.value?.click();
}

function goToLogin() {
    router.push('/login');
}
</script>

<template>
    <div class="relative min-h-screen w-screen flex flex-col overflow-hidden">
        <div class="relative flex-1 flex items-center justify-center px-4">
            <!-- Background -->
            <div class="absolute inset-0 w-full h-full z-0">
                <AmbientFabricBackground :settings="{ class: 'w-full h-full' }" />
            </div>

            <!-- Loading State -->
            <div 
                v-if="isValidating" 
                class="relative z-10 flex flex-col gap-8 bg-white rounded-md shadow-md p-8 w-full max-w-md"
            >
                <UiLogo size="md" />
                <div class="flex flex-col gap-2">
                    <UiText as="h2" variant="h3" class="font-semibold">
                        <span>Validating invitation...</span>
                    </UiText>
                    <UiText as="p" variant="p" class="text-se-gray-600">
                        <span>Please wait while we verify your invitation link.</span>
                    </UiText>
                </div>
            </div>

            <!-- Error State -->
            <div 
                v-else-if="isValidationError" 
                class="relative z-10 flex flex-col gap-8 bg-white rounded-md shadow-md p-8 w-full max-w-md"
            >
                <UiLogo size="md" />
                <div class="flex flex-col gap-2">
                    <UiText as="h2" variant="h3" class="font-semibold">
                        <span>Invalid invitation</span>
                    </UiText>
                    <UiText as="p" variant="p" class="text-se-gray-600">
                        <span>{{ errorMessage }}</span>
                    </UiText>
                </div>
                
                <div class="flex flex-col gap-3">
                    <UiButton
                        v-if="isUserExistsError"
                        type="button"
                        variant="primary"
                        label="Go to login"
                        class="w-full"
                        @click="goToLogin"
                    />
                    <UiButton
                        v-else
                        type="button"
                        variant="outline"
                        label="Back to login"
                        class="w-full"
                        @click="goToLogin"
                    />
                </div>
            </div>

            <!-- No Token State -->
            <div 
                v-else-if="!token" 
                class="relative z-10 flex flex-col gap-8 bg-white rounded-md shadow-md p-8 w-full max-w-md"
            >
                <UiLogo size="md" />
                <div class="flex flex-col gap-2">
                    <UiText as="h2" variant="h3" class="font-semibold">
                        <span>Missing invitation token</span>
                    </UiText>
                    <UiText as="p" variant="p" class="text-se-gray-600">
                        <span>Please use the link from your invitation email to access this page.</span>
                    </UiText>
                </div>
                
                <UiButton
                    type="button"
                    variant="outline"
                    label="Back to login"
                    class="w-full"
                    @click="goToLogin"
                />
            </div>

            <!-- Success State: Password Form -->
            <div 
                v-else-if="inviteData" 
                class="relative z-10 flex flex-col gap-8 bg-white rounded-md shadow-md p-8 w-full max-w-md"
            >
                <!-- Logo -->
                <UiLogo size="md" />

                <!-- Header -->
                <div class="flex flex-col gap-2">
                    <UiText as="h2" variant="h3" class="font-semibold">
                        <span>Your email has been verified successfully</span>
                    </UiText>
                    <UiText as="p" variant="p" class="text-se-gray-600">
                        <span>Create a password to complete your account, and access your dashboard to track progress and review proposals.</span>
                    </UiText>
                </div>

                <!-- Password Form -->
                <UiForm v-model="formModel" :schema="formSchema" @submit="onSubmit">
                    <div class="flex flex-col gap-5">
                        <button ref="submitRef" type="submit" class="hidden" />

                        <UiFormField name="password" label="Password" type="password">
                            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                                <UiInputField
                                    :id="id"
                                    name="password"
                                    type="password"
                                    :model-value="modelValue as string"
                                    :invalid="invalid"
                                    :error="error"
                                    :disabled="isLoading"
                                    placeholder="Choose your password"
                                    @update:model-value="onUpdateModelValue"
                                    @validate="onValidate"
                                />
                            </template>
                        </UiFormField>

                        <UiFormField name="password_confirmation" label="Confirm Password" type="password">
                            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                                <UiInputField
                                    :id="id"
                                    name="password_confirmation"
                                    type="password"
                                    :model-value="modelValue as string"
                                    :invalid="invalid"
                                    :error="error"
                                    :disabled="isLoading"
                                    placeholder="Re-type password"
                                    @update:model-value="onUpdateModelValue"
                                    @validate="onValidate"
                                />
                            </template>
                        </UiFormField>

                        <UiButton
                            type="button"
                            variant="primary"
                            label="Set password"
                            class="w-full"
                            :disabled="isLoading"
                            @click="handleSubmit"
                        />

                        <UiButton
                            type="button"
                            variant="outline"
                            label="Back to login"
                            class="w-full"
                            :disabled="isLoading"
                            @click="goToLogin"
                        />
                    </div>
                </UiForm>
            </div>
        </div>
    </div>
</template>
